// Life-insurance retention/chargeback money math — CANONICAL location.
//
// WHY THIS FILE EXISTS
// ──────────────────────────────────────────────────────────────────
// The Retention & Chargeback Command Center (RetentionCommandCenterView,
// CommissionTracker.jsx) needs a top-metrics strip (active policies,
// premium on book, persistency 30/60/90/180/365, chargebacks, revenue
// at risk, etc). That money math ALREADY lives inline inside four
// existing components:
//   - RetentionHub    (active/lapsed classification + premium-on-book)
//   - PersistencyView (per-policy issue/lapse aging + N-day windows)
//   - ChargebacksView (chargeback grouping + $ loss)
//   - RetentionView   (advance-in-chargeback-window $ exposure)
//
// Per doctrine, a second copy of money math that can silently drift
// from the original is the worst possible outcome. These functions
// are VERBATIM extractions of those components' internal formulas
// (same grouping keys, same filters, same field names) so the Command
// Center's top-metrics strip renders the identical numbers those
// components already compute — not a re-derivation. Every function is
// locked by test/lifeExposure.test.js against a fixture BEFORE it was
// wired into any UI.
//
// `parseDate` / `isInChargebackWindow` / `conservationPriority` are
// imported from the already-shared src/lib/dates.js and
// src/lib/parseConservation.js — the SAME functions RetentionView
// itself imports — not reimplemented here.
//
// ESM (`export`), same as src/lib/dates.js and src/lib/parseConservation.js
// (CommonJS-style `module.exports` files in this repo trip a webpack
// CJS-named-export static-analysis issue that isn't worth fighting;
// dates.js/parseConservation.js already prove ESM is the working
// pattern for files consumed by CommissionTracker.jsx's `import {...}`).
// Node-testability comes from test/support/registerJsx.js (Babel
// on-the-fly compile), the same mechanism test/no_use_before_define.test.js
// already uses to require CommissionTracker.jsx itself from plain Node.
//
// Pure functions only. No React, no Supabase, no network calls — this
// file must stay side-effect-free so it's safe to unit test and safe
// to reuse from anywhere. (test/lifeExposure.test.js includes a static
// guard asserting no supabase/network import ever lands here — if this
// file needs live data it must take it as an argument, not fetch it.)

import { parseDate } from './dates';
import { isInChargebackWindow, conservationPriority } from './parseConservation';

// ── Chargeback activity-type classification ──────────────────────
// Moved verbatim from CommissionTracker.jsx (was a local `const
// CHARGEBACK_TYPES = new Set([...])`) so this is the ONE definition —
// CommissionTracker.jsx now imports it from here instead of keeping
// its own copy.
export const CHARGEBACK_TYPES = new Set([
  // MOO statement activity types
  'LAPSE', 'LAPSE REV ADV', 'RECPT ADV CO', 'BFY REVERSAL', 'CANC EFF ISS', 'NEW ISS REV',
  // Americo statement activity labels (mapped from Trx codes)
  'CHARGEBACK - LAPSE', 'CHARGEBACK - SURRENDER', 'CHARGEBACK - DEATH',
  'CHARGEBACK - ADVANCE LAPSE', 'CHARGEBACK - ADVANCE REISSUE',
]);

// ── 1. Active/lapsed book classification + premium on book ───────
// Mirrors RetentionHub's `activeClients`/`lapsedClients` useMemo
// (CommissionTracker.jsx, RetentionHub function). One row per
// (prodNum, mga, policy). A policy with any chargeback OR negative
// net commission counts as lapsed.
function buildPolicyBook(transactions) {
  const policies = {};
  for (const tx of (transactions || [])) {
    if (!tx.policy) continue;
    const k = `${tx.prodNum || ''}|${tx.mga || ''}|${tx.policy}`;
    if (!policies[k]) {
      policies[k] = {
        key: k,
        prodNum: tx.prodNum,
        policy: tx.policy,
        insured: tx.insured || '',
        issueDate: tx.issueDate || '',
        firstLapseDate: '',
        chargebackCount: 0,
        totalCommission: 0,
        monthlyPremium: 0,
      };
    }
    const p = policies[k];
    if (!p.insured && tx.insured) p.insured = tx.insured;
    if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
    if (tx.commPremium && Math.abs(tx.commPremium) > p.monthlyPremium) p.monthlyPremium = Math.abs(tx.commPremium);
    p.totalCommission += Number(tx.netToAccount) || 0;
    const isCb = CHARGEBACK_TYPES.has(tx.activityType) || ((tx.netToAccount || 0) < 0);
    if (isCb && tx.activityDate) {
      p.chargebackCount++;
      if (!p.firstLapseDate || (parseDate(tx.activityDate)?.getTime() || 0) < (parseDate(p.firstLapseDate)?.getTime() || 0)) {
        p.firstLapseDate = tx.activityDate;
      }
    }
  }
  return Object.values(policies);
}

export function classifyLifeBook(transactions) {
  const all = buildPolicyBook(transactions);
  const lapsedPolicies = all.filter(p => p.chargebackCount > 0 || p.totalCommission < 0);
  const activePolicies = all.filter(p => p.chargebackCount === 0 && p.totalCommission >= 0);
  const monthlyPremiumOnBook = activePolicies.reduce((s, p) => s + (p.monthlyPremium || 0), 0);
  const total = activePolicies.length + lapsedPolicies.length;
  return {
    activePolicies,
    lapsedPolicies,
    activeCount: activePolicies.length,
    lapsedCount: lapsedPolicies.length,
    monthlyPremiumOnBook,
    annualPremiumOnBook: monthlyPremiumOnBook * 12,
    persistencyRatePct: total > 0 ? (activePolicies.length / total) * 100 : null,
  };
}

// ── 2. Persistency at N days ──────────────────────────────────────
// Mirrors PersistencyView's `policies` useMemo + `computeWindow`
// (CommissionTracker.jsx, PersistencyView function). computeWindow is
// generic over N, so adding N=60 alongside the view's existing
// 30/90/180/365 is the SAME formula at a different window, not a new
// derivation.
export function buildPersistencyPolicies(transactions, today = new Date()) {
  const dayMs = 1000 * 60 * 60 * 24;
  const map = {};
  (transactions || []).forEach(tx => {
    if (!tx.policy) return;
    const key = `${tx.prodNum || ''}|${tx.mga || ''}|${tx.policy}`;
    if (!map[key]) {
      map[key] = {
        key, policy: tx.policy, insured: tx.insured || '', prodNum: tx.prodNum || '',
        issueDate: tx.issueDate || '', firstLapseDate: '', lapsed: false,
        totalCommission: 0, premium: 0,
      };
    }
    const p = map[key];
    if (!p.insured && tx.insured) p.insured = tx.insured;
    if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
    if (tx.commPremium && Math.abs(tx.commPremium) > p.premium) p.premium = Math.abs(tx.commPremium);
    p.totalCommission += Number(tx.netToAccount) || 0;
    const isLapseEvent = CHARGEBACK_TYPES.has(tx.activityType) || (Number(tx.netToAccount) || 0) < 0;
    if (isLapseEvent && tx.activityDate) {
      p.lapsed = true;
      if (!p.firstLapseDate || (parseDate(tx.activityDate)?.getTime() || 0) < (parseDate(p.firstLapseDate)?.getTime() || 0)) {
        p.firstLapseDate = tx.activityDate;
      }
    }
  });
  return Object.values(map).map(p => {
    const issued = parseDate(p.issueDate);
    const lapsedDate = parseDate(p.firstLapseDate);
    const policyAgeDays = issued ? Math.floor((today.getTime() - issued.getTime()) / dayMs) : null;
    const daysToLapse = (p.lapsed && issued && lapsedDate) ? Math.floor((lapsedDate.getTime() - issued.getTime()) / dayMs) : null;
    return { ...p, issued, lapsedDate, policyAgeDays, daysToLapse };
  }).filter(p => p.issueDate);
}

export function computePersistencyWindow(N, policies) {
  const eligible = policies.filter(p => p.policyAgeDays != null && p.policyAgeDays >= N);
  const lapsedWithin = eligible.filter(p => p.lapsed && p.daysToLapse != null && p.daysToLapse <= N);
  const survived = eligible.length - lapsedWithin.length;
  const pct = eligible.length > 0 ? (survived / eligible.length) * 100 : null;
  return { N, eligibleCount: eligible.length, lapsedCount: lapsedWithin.length, survived, pct };
}

export function computePersistencyWindows(transactions, windows = [30, 60, 90, 180, 365], today = new Date()) {
  const policies = buildPersistencyPolicies(transactions, today);
  const result = {};
  for (const n of windows) result[n] = computePersistencyWindow(n, policies);
  return { policies, windows: result };
}

// ── 3. Chargeback grouping + $ loss ───────────────────────────────
// Mirrors ChargebacksView's `policyMap`/`policies` build
// (CommissionTracker.jsx, ChargebacksView function).
export function groupChargebackPolicies(transactions) {
  const cbRows = (transactions || []).filter(tx =>
    CHARGEBACK_TYPES.has(tx.activityType) || (tx.netToAccount || 0) < 0
  );
  const policyMap = {};
  cbRows.forEach(tx => {
    const key = `${tx.mga || ''}|${tx.policy || ''}|${tx.prodNum || ''}`;
    if (!policyMap[key]) {
      policyMap[key] = {
        key, policy: tx.policy, insured: tx.insured, prodNum: tx.prodNum, mga: tx.mga,
        company: tx.company, state: tx.state, issueDate: tx.issueDate,
        earliestCbDate: tx.activityDate, totalLoss: 0, activities: new Set(),
      };
    }
    const p = policyMap[key];
    p.totalLoss += tx.netToAccount;
    p.activities.add(tx.activityType);
    const existing = parseDate(p.earliestCbDate);
    const current = parseDate(tx.activityDate);
    if (current && (!existing || current < existing)) p.earliestCbDate = tx.activityDate;
    if (!p.issueDate && tx.issueDate) p.issueDate = tx.issueDate;
  });
  const policies = Object.values(policyMap).map(p => {
    const issue = parseDate(p.issueDate);
    const cb = parseDate(p.earliestCbDate);
    const days = (issue && cb) ? Math.floor((cb - issue) / (1000 * 60 * 60 * 24)) : null;
    return { ...p, days, activitiesList: Array.from(p.activities) };
  });
  const totalLoss = policies.reduce((s, p) => s + p.totalLoss, 0);
  return { policies, count: policies.length, totalLoss };
}

// ── 4. Advance-in-chargeback-window $ exposure ────────────────────
// Mirrors RetentionView's `txByPolicy`/`decorated`/`dollarsAtRisk`
// build (CommissionTracker.jsx, RetentionView function). Operates on
// the paste/upload-ingested `conservation` events array (the carrier
// conservation-report worklist), cross-referenced against commission
// transactions to find $ already advanced on each open case.
export function computeConservationExposure(conservationEvents, transactions, today = new Date()) {
  const txByPolicy = {};
  (transactions || []).forEach(tx => {
    const p = String(tx.policy || '').trim();
    if (!p) return;
    if (!txByPolicy[p] || Number(tx.commPremium) > Number(txByPolicy[p].commPremium)) txByPolicy[p] = tx;
  });
  const decorated = (conservationEvents || []).map(ev => {
    const priority = conservationPriority(ev, today);
    const inWindow = isInChargebackWindow(ev, today);
    const matchTx = ev.policyNum ? txByPolicy[ev.policyNum] : null;
    return {
      ...ev,
      priority,
      inChargebackWindow: inWindow,
      advancedPremium: matchTx ? Number(matchTx.commPremium) || 0 : null,
    };
  });
  const openEvents = decorated.filter(e => e.status === 'open');
  const dollarsAtRisk = openEvents
    .filter(e => e.inChargebackWindow && e.advancedPremium)
    .reduce((s, e) => s + (e.advancedPremium || 0), 0);
  const totalAdvancedOpen = openEvents
    .filter(e => e.advancedPremium)
    .reduce((s, e) => s + (e.advancedPremium || 0), 0);
  const failedDrafts  = openEvents.filter(e => e.eventType === 'nsf');
  const pendingLapses = openEvents.filter(e => e.eventType === 'lapse_warning');
  const gracePeriod   = openEvents.filter(e => e.eventType === 'grace_period');
  const returnedMail  = openEvents.filter(e => e.eventType === 'returned_mail');
  const cured         = decorated.filter(e => e.status === 'cured');
  const dismissed     = decorated.filter(e => e.status === 'dismissed');
  const recoveryRatePct = (cured.length + dismissed.length) > 0
    ? (cured.length / (cured.length + dismissed.length)) * 100
    : null;
  return {
    decorated, openEvents, dollarsAtRisk, totalAdvancedOpen,
    failedDrafts, pendingLapses, gracePeriod, returnedMail,
    cured, dismissed, recoveryRatePct,
  };
}

// ── 5. Chargeback root-cause — HARD no-fabrication guard ──────────
// Deterministic (NOT an LLM call), on purpose: the only way to
// guarantee "the AI root-cause may not assert a reason the
// carrier/client record doesn't support" is to make the derivation
// structurally incapable of inventing text that isn't already present
// in one of the two grounding fields. Every returned `text` is either
// (a) a category label whose keyword regex matched a substring of the
// grounding text, tagged with which record it came from, or (b) a
// verbatim echo of the grounding text. If neither field has content,
// it returns the fixed "not determined" string — never a guess.
const CB_KEYWORD_RULES = [
  { re: /\bnsf\b|non-?sufficient|insufficient funds/i, label: 'Payment failure (NSF) triggered the chargeback' },
  { re: /free.?look/i,           label: 'Client exercised free-look cancellation rights' },
  { re: /replace/i,              label: 'Policy replaced by another carrier/product' },
  { re: /\bdeath\b/i,            label: 'Death claim triggered the chargeback' },
  { re: /surrender|cancel/i,     label: 'Client-initiated cancellation/surrender' },
  { re: /lapse/i,                label: 'Policy lapsed for non-payment before the chargeback window closed' },
  { re: /returned mail|bad address/i, label: 'Returned mail — carrier could not reach the client to collect payment' },
];

export function deriveChargebackRootCause({ carrierReason, clientReason } = {}) {
  const carrier = String(carrierReason || '').trim();
  const client  = String(clientReason || '').trim();
  if (!carrier && !client) {
    return {
      determined: false,
      groundedIn: null,
      text: 'Not determined — no carrier or client-reported reason on file for this case.',
    };
  }
  const sources = [{ text: carrier, label: 'carrier' }, { text: client, label: 'client' }];
  for (const src of sources) {
    if (!src.text) continue;
    for (const rule of CB_KEYWORD_RULES) {
      if (rule.re.test(src.text)) {
        return { determined: true, groundedIn: src.label, text: `${rule.label} (per ${src.label} record).` };
      }
    }
  }
  // No keyword matched either field — echo verbatim rather than guess
  // a category. Carrier record wins when both are present, since it's
  // the record of the actual money movement.
  if (carrier) return { determined: true, groundedIn: 'carrier', text: `Per carrier record: "${carrier}"` };
  return { determined: true, groundedIn: 'client', text: `Per client-reported note: "${client}"` };
}
